import { Form, useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'

import { Field } from '#app/components/form/field.tsx'
import { PhoneField } from '#app/components/form/phone-field.tsx'
import { Page } from '#app/components/page.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Divider } from '#app/components/ui/divider.tsx'
import { type Route } from '#app/routes/mocra+/_form+/+types/personal-info.ts'
import { Section } from '#app/routes/mocra+/_form+/components/section.tsx'
import {
	buildStepPathFromId,
	getNextFormStepId,
} from '#app/routes/mocra+/_form+/form-steps.ts'
import { useContactDirectory } from '#app/routes/mocra+/_form+/hooks/use-contact-directory.ts'
import { useSectionForm } from '#app/routes/mocra+/_form+/hooks/use-section-form.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'MoCRA — Your Information' },
]

const PersonalInfoSchema = z.object({
	name: z
		.string({
			required_error: "We'd love to know your name.",
		})
		.min(1, "We'd love to know your name."),
	email: z
		.string({
			required_error:
				"What's the best email address where we can reach you if needed?",
		})
		.email('Please enter a valid email address so we can stay in touch.'),
	title: z.string().optional(),
	phone: z.string().optional(),
})

export type PersonalInfoFormValues = z.infer<typeof PersonalInfoSchema>

const DEFAULT_VALUES: PersonalInfoFormValues = {
	name: '',
	email: '',
	title: '',
	phone: '',
}

const SECTION_KEY = 'personalInfo'
const STEP_ID = 'personal-info'

export default function PersonalInfo() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const { upsertContact } = useContactDirectory()

	const {
		values,
		setFieldValue,
		getFieldErrors,
		submit,
		status,
		hasStoredValue,
		clear,
	} = useSectionForm({
		section: SECTION_KEY,
		schema: PersonalInfoSchema,
		defaultValue: DEFAULT_VALUES,
		onValidSubmit: (validatedValues) => {
			// Save to contact directory - upsertContact is idempotent and will merge by email
			upsertContact(
				{
					name: validatedValues.name,
					email: validatedValues.email,
					title: validatedValues.title,
					phone: validatedValues.phone,
					source: 'Form submitter',
				},
				{ source: 'Form submitter' },
			)

			const nextStepId = getNextFormStepId(STEP_ID)
			if (!nextStepId) return
			const nextHref = buildStepPathFromId(nextStepId, searchParams)
			void navigate(nextHref)
		},
	})

	const nameErrors = getFieldErrors('name')
	const emailErrors = getFieldErrors('email')
	const titleErrors = getFieldErrors('title')
	const phoneErrors = getFieldErrors('phone')

	return (
		<Page
			title="Your information"
			description="We need to collect your personal information to contact you later on about your MoCRA compliance package."
			className="space-y-8"
		>
			<Form
				method="post"
				className="space-y-8"
				onSubmit={(event) => {
					event.preventDefault()
					void submit()
				}}
			>
				<Divider />

				<Section
					title="Personal info"
					description="Basic information to help us contact you and personalize your compliance documents."
				>
					<Field
						labelProps={{
							children: "What's your name?",
						}}
						inputProps={{
							name: 'name',
							value: values.name,
							onChange: (event) => setFieldValue('name', event.target.value),
							type: 'text',
						}}
						errors={nameErrors.length > 0 ? [...nameErrors] : undefined}
					/>
					<Field
						labelProps={{
							children: "What's your work email?",
						}}
						inputProps={{
							name: 'email',
							value: values.email,
							onChange: (event) => setFieldValue('email', event.target.value),
							type: 'email',
						}}
						errors={emailErrors.length > 0 ? [...emailErrors] : undefined}
					/>
					<Field
						labelProps={{
							children: "What's your job title?",
						}}
						inputProps={{
							name: 'title',
							value: values.title,
							onChange: (event) => setFieldValue('title', event.target.value),
							type: 'text',
						}}
						errors={titleErrors.length > 0 ? [...titleErrors] : undefined}
					/>
					<PhoneField
						labelProps={{
							children: "What's your phone number? (Optional)",
						}}
						inputProps={{
							name: 'phone',
							value: values.phone,
							onChange: (event) => setFieldValue('phone', event.target.value),
						}}
						errors={phoneErrors.length > 0 ? [...phoneErrors] : undefined}
					/>
				</Section>

				{status === 'invalid' ? (
					<p className="text-destructive text-sm">
						Please fill in the required fields before continuing.
					</p>
				) : null}

				<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
					{hasStoredValue ? (
						<Button type="button" variant="outline" onClick={() => clear()}>
							Clear answers
						</Button>
					) : (
						<span />
					)}
					<Button type="submit" className="w-full sm:w-auto">
						Continue
					</Button>
				</div>
			</Form>
		</Page>
	)
}
